import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { DropDownItem } from '../../../models/shared/stylesheet/drop-down-item';
import { TableColumnHeaderSearchType } from '../../../models/enum/shared/table-column-header-search-type';
import { TableHeaderTextInputComponent } from '../table-column-inputs/table-header-text-input/table-header-text-input.component';
import { TableHeaderNumberInputComponent } from '../table-column-inputs/table-header-number-input/table-header-number-input.component';
import { TableHeaderDateInputComponent } from '../table-column-inputs/table-header-date-input/table-header-date-input.component';
import { TableHeaderSelectInputComponent } from '../table-column-inputs/table-header-select-input/table-header-select-input.component';

@Component({
  selector: 'app-table-column-header',
  standalone: true,
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    AsyncPipe,
    ReactiveFormsModule,
    TableHeaderTextInputComponent,
    TableHeaderNumberInputComponent,
    TableHeaderDateInputComponent,
    TableHeaderSelectInputComponent
  ],
  templateUrl: './table-column-header.component.html',
  styleUrls: ['./table-column-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class TableColumnHeaderComponent implements OnChanges {
  @Input() odataSortProperty: string = '';
  @Input() columnName: string = '';
  @Input() sortString: string = '';
  @Input() controlId: string = '';
  @Input() dropdownItems: DropDownItem[] = [];
  @Input() searchType: TableColumnHeaderSearchType = TableColumnHeaderSearchType.Text;
  @Input() formGroup!: FormGroup;
  @Input() disableSort: boolean = false;
  @Input() justifyColumnName: string = 'start';
  @Output() updateActiveSort: EventEmitter<string> = new EventEmitter();

  private _activeSort = new BehaviorSubject<string>('');
  public readonly activeSort$ = this._activeSort as Observable<string>;

  private _dropdownItems = new BehaviorSubject<DropDownItem[]>([]);
  public readonly dropdownItems$ = this._dropdownItems as Observable<DropDownItem[]>;

  public isAscSort$ = this.activeSort$.pipe(map(sort => sort === `${this.odataSortProperty} asc`));
  public isDescSort$ = this.activeSort$.pipe(map(sort => sort === `${this.odataSortProperty} desc`));

  public sortIconSrc$ = combineLatest([this.isAscSort$, this.isDescSort$]).pipe(
    map(([isAsc, isDesc]) => {
      if (isAsc) {
        return 'assets/icons/white/Arrow Up.svg';
      } else if (isDesc) {
        return 'assets/icons/white/Arrow Down.svg';
      }
      return 'assets/icons/blue/Arrow Down.svg';
    })
  );

  public toggleSort(): void {
    combineLatest([this.isAscSort$, this.isDescSort$]).once(([isAsc, isDesc]) => {
      if (this.disableSort) return;
      if (isDesc) {
        this.updateActiveSort.emit('');
      } else if (isAsc) {
        const newSort = `${this.odataSortProperty} desc`;
        this.updateActiveSort.emit(newSort);
      } else {
        const newSort = `${this.odataSortProperty} asc`;
        this.updateActiveSort.emit(newSort);
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.sortString) {
      this._activeSort.next(this.sortString);
    }
    if (changes.dropdownItems) {
      this._dropdownItems.next(this.dropdownItems);
    }
  }

  protected readonly TableColumnHeaderSearchType = TableColumnHeaderSearchType;
}
